import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { categories, investorResources, relocationGuide } from '../data/categories';

// Fix for Leaflet marker icons
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const HomePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [mapCenter] = useState([18.1096, -77.2975]); // Jamaica's coordinates
  const [featuredListings, setFeaturedListings] = useState([]);

  useEffect(() => {
    // Generate featured listings from all categories
    const featured = [];
    categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        // Get the highest rated item from each subcategory
        const highestRated = [...subcategory.items].sort((a, b) => b.rating - a.rating)[0];
        if (highestRated) {
          featured.push({
            ...highestRated,
            category: category.id,
            subcategory: subcategory.id,
            categoryTitle: category.title,
            subcategoryTitle: subcategory.title
          });
        }
      });
    });
    
    // Limit to 6 featured listings
    setFeaturedListings(featured.slice(0, 6));
  }, []);

  return (
    <div>
      {/* Hero Section with Search and Map */}
      <section className="bg-primary text-white py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h1 className="display-4 fw-bold mb-4">Discover Jamaica</h1>
              <p className="lead mb-4">
                Your comprehensive directory for historic sites, restaurants, pet services, marinas, golf courses, and more.
              </p>
              <Form className="d-flex">
                <Form.Control
                  type="search"
                  placeholder="Search for anything in Jamaica..."
                  className="me-2"
                  size="lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button 
                  variant="light" 
                  size="lg"
                  as={Link}
                  to={`/search?q=${encodeURIComponent(searchTerm)}`}
                  disabled={!searchTerm.trim()}
                >
                  Search
                </Button>
              </Form>
            </Col>
            <Col lg={6}>
              <div style={{ height: '400px', width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
                <MapContainer 
                  center={mapCenter} 
                  zoom={8} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {featuredListings.map((listing) => (
                    <Marker 
                      key={`${listing.category}-${listing.id}`}
                      position={[
                        18.1096 + (Math.random() - 0.5) * 0.5, // Random position for demo
                        -77.2975 + (Math.random() - 0.5) * 0.5
                      ]}
                    >
                      <Popup>
                        <strong>{listing.name}</strong><br />
                        {listing.location}<br />
                        Rating: {listing.rating}/5<br />
                        <Link to={`/listing/${listing.category}/${listing.subcategory}/${listing.id}`}>
                          View Details
                        </Link>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Categories Section */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-5">Explore Categories</h2>
          <Row>
            {categories.map((category) => (
              <Col key={category.id} md={4} className="mb-4">
                <Card className="h-100 shadow-sm hover-card">
                  <Card.Body>
                    <Card.Title>{category.title}</Card.Title>
                    <Card.Text>{category.description}</Card.Text>
                    <Button 
                      variant="outline-primary" 
                      as={Link} 
                      to={`/category/${category.id}`}
                    >
                      Explore {category.title}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Featured Listings Section */}
      <section className="py-5 bg-light">
        <Container>
          <h2 className="text-center mb-5">Featured Listings</h2>
          <Row>
            {featuredListings.map((listing) => (
              <Col key={`${listing.category}-${listing.id}`} md={4} className="mb-4">
                <Card className="h-100 shadow-sm hover-card">
                  <Card.Body>
                    <span className="badge bg-primary mb-2">{listing.categoryTitle}</span>
                    <Card.Title>{listing.name}</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">{listing.location}</Card.Subtitle>
                    <div className="mb-2">
                      Rating: {listing.rating}/5
                    </div>
                    <Card.Text>
                      {listing.subcategoryTitle}
                    </Card.Text>
                    <Button 
                      variant="outline-primary" 
                      as={Link} 
                      to={`/listing/${listing.category}/${listing.subcategory}/${listing.id}`}
                    >
                      View Details
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Resources Section */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-5">Resources</h2>
          <Row>
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm hover-card">
                <Card.Body>
                  <Card.Title>{investorResources.title}</Card.Title>
                  <Card.Text>{investorResources.description}</Card.Text>
                  <Button 
                    variant="outline-primary" 
                    as={Link} 
                    to="/investor-resources"
                  >
                    View Investor Resources
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm hover-card">
                <Card.Body>
                  <Card.Title>{relocationGuide.title}</Card.Title>
                  <Card.Text>{relocationGuide.description}</Card.Text>
                  <Button 
                    variant="outline-primary" 
                    as={Link} 
                    to="/relocation-guide"
                  >
                    View Relocation Guide
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HomePage;
